import { Card } from '@mui/material';
import { useHover } from '@react-aria/interactions';
import { Button, MoreMenu, Text } from '@shared/components';
import { OptionProps } from '@shared/components/more-menu/MoreMenu';
import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import { ConversationEventComponent } from '@shared/features/conversation-event/ConversationEvent';
import { ActionExecutionEventType } from '@shared/generated/graphql';
import { ActionExecutionEvent } from '@shared/types/action-execution';
import {
  ChatMessageEvent,
  ConversationContributionRole,
  ConversationEvent,
  ConversationMessageEvent,
  ConversationMessageEventType,
  EmailContribution,
  isQuietLineIntentEventType,
  SystemMessageEvent,
  UnknownEvent,
  VoiceContribution,
} from '@shared/types/conversation';
import { makeElementClassNameFactory, makeRootClassName } from '@shared/utils';
import { assertUnreachable } from 'clerk_common/enum';
import {
  formatAbsoluteTime,
  formatElapsedTime,
} from 'clerk_common/stringification/times';
import clsx from 'clsx';
import DOMPurify from 'dompurify';
import { isNil } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from 'react-feather';
import { LuEllipsisVertical } from 'react-icons/lu';
import { ActionExecutionEventComponent } from '../../../action-execution-event/ActionExecutionEvent';
import { SaveSnapshotToDatasetModal } from '../save-snapshot-to-dataset/SaveSnapshotToDatasetModal';
import { AwkwardMomentLabelModal } from './AwkwardMomentLabelModal';
import { ChatContribution } from './ChatContribution';
import { VoiceContributionTranscript } from './VoiceContributionTranscript';
import { useConversationMessages } from './useConversationMessages';
import {
  getRecordingStartTime,
  secondsBetween,
  useTranscriptAdminActions,
} from './useTranscriptAdminActions';

const ROOT = makeRootClassName('ConversationTranscript');
const el = makeElementClassNameFactory(ROOT);

type ConversationMetadata = {
  id: string;
  createdAt: Date;
  mediaUrl?: string;
  events?: ConversationEvent[];
};

export const includeMessage = <T extends TranscriptMessage>(
  item: T
): boolean => {
  const messageType = item.message.type;

  if (
    [
      ConversationMessageEventType.VOICE_CONTRIBUTION,
      ConversationMessageEventType.EMAIL_CONTRIBUTION,
    ].includes(messageType)
  ) {
    return true;
  }

  if (messageType === ConversationMessageEventType.CHAT_MESSAGE) {
    return (
      [
        ConversationContributionRole.USER,
        ConversationContributionRole.ASSISTANT,
      ].includes(item.message.role) && isNil(item.message.canonicalMessageId)
    );
  }
  return false;
};

const includeItem = (item: TranscriptItem): boolean => {
  const isMessage = item.type === 'message';
  if (!isMessage) return false;
  return includeMessage(item);
};

export const ConversationTranscript = ({
  conversation,
  actionExecutionEvents,
  startDate,
  endDate,
  scrollToBottomFn,
  className,
  aiSide,
  suppressSystemMessages,
  emptyStateMessage,
  onSeekToSeconds,
}: {
  conversation: ConversationMetadata;
  actionExecutionEvents: ActionExecutionEvent[];
  startDate?: Date;
  endDate?: Date | null;
  scrollToBottomFn?: () => void;
  className?: string;
  aiSide?: 'left' | 'right';
  suppressSystemMessages?: boolean;
  emptyStateMessage?: string;
  onSeekToSeconds?: (seconds: number) => void;
}) => {
  const { isVoomaAdmin } = useMeContext();
  const { msgs, events } = useConversationMessages(conversation.id);
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    awkwardMomentModalState,
    closeAwkwardMomentModal,
    handleCreateAwkwardMomentLabel,
    datasetModalState,
    closeDatasetModal,
    handleSaveSnapshotToDataset,
    makeOptions,
  } = useTranscriptAdminActions({
    conversation,
    isVoomaAdmin,
  });

  useEffect(() => {
    if (scrollToBottomFn) {
      scrollToBottomFn();
      return;
    }
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [msgs.length, events.length]);

  const thisConversationMsgs = msgs.filter(
    messageProbablyBelongsToThisExecution(startDate, endDate)
  );

  const thisConversationEvents = events.filter(
    (event) =>
      !isQuietLineIntentEventType(event.name) &&
      conversationEventProbablyBelongsToThisExecution(startDate, endDate)(event)
  );

  const showRestrictedEvents = isVoomaAdmin;
  const msgsAndEvents = combineMessagesAndEvents(
    thisConversationMsgs,
    thisConversationEvents,
    actionExecutionEvents,
    showRestrictedEvents
  );
  const visibleMessages = suppressSystemMessages
    ? msgsAndEvents.filter(includeItem)
    : msgsAndEvents;
  const timeOrigin =
    (onSeekToSeconds && getRecordingStartTime(conversation.events)) ||
    conversation.createdAt;

  return (
    <>
      <div
        ref={containerRef}
        className={clsx(el`conversation-events`, className)}
      >
        {visibleMessages.map((e) => (
          <TranscriptItemComponent
            key={getItemId(e)}
            item={e}
            conversationStartTime={timeOrigin}
            moreMenuOptions={makeOptions(e)}
            aiSide={aiSide}
            suppressSystemMessages={suppressSystemMessages}
            onSeekToSeconds={onSeekToSeconds}
          />
        ))}
        {visibleMessages.length === 0 && (
          <Text type="body-xs" className="py-2 text-gray-500">
            {emptyStateMessage ?? 'Waiting for someone to say something...'}
          </Text>
        )}
      </div>
      <SaveSnapshotToDatasetModal
        isOpen={datasetModalState !== null}
        setIsOpen={(isOpen) => {
          if (!isOpen) closeDatasetModal();
        }}
        onSave={handleSaveSnapshotToDataset}
      />
      <AwkwardMomentLabelModal
        isOpen={awkwardMomentModalState !== null}
        initialPlaybackSeconds={awkwardMomentModalState?.playbackSeconds ?? 0}
        audioUrl={conversation.mediaUrl}
        onClose={closeAwkwardMomentModal}
        onSave={handleCreateAwkwardMomentLabel}
      />
    </>
  );
};

function getItemId(item: TranscriptItem) {
  switch (item.type) {
    case 'message':
      return item.message.id;
    case 'conversationEvent':
      return item.event.id;
    case 'event':
      return item.event.id;
    default:
      return assertUnreachable(item);
  }
}

function TranscriptItemComponent(p: {
  item: TranscriptItem;
  conversationStartTime: Date;
  moreMenuOptions: OptionProps[];
  aiSide?: 'left' | 'right';
  suppressSystemMessages?: boolean;
  onSeekToSeconds?: (seconds: number) => void;
}) {
  switch (p.item.type) {
    case 'message':
      return (
        <ConversationMessageEventComponent
          event={p.item.message}
          conversationStartTime={p.conversationStartTime}
          moreMenuOptions={p.moreMenuOptions}
          aiSide={p.aiSide}
          suppressSystemMessages={p.suppressSystemMessages}
          onSeekToSeconds={p.onSeekToSeconds}
        />
      );
    case 'event':
      return <ActionExecutionEventComponent event={p.item.event} />;
    case 'conversationEvent':
      return <ConversationEventComponent event={p.item.event} />;
    default:
      return assertUnreachable(p.item);
  }
}

export type TranscriptMessage = {
  type: 'message';
  message: ConversationMessageEvent;
};

type TranscriptConversationEvent = {
  type: 'conversationEvent';
  event: ConversationEvent;
};

type TranscriptEvent = {
  type: 'event';
  event: ActionExecutionEvent;
};

type TranscriptItem =
  | TranscriptMessage
  | TranscriptConversationEvent
  | TranscriptEvent;

function combineMessagesAndEvents(
  messages: ConversationMessageEvent[],
  convEvents: ConversationEvent[],
  aeEvents: ActionExecutionEvent[],
  showRestrictedEvents: boolean
): TranscriptItem[] {
  const msgs = messages.map((m) => ({
    type: 'message' as const,
    message: m,
  }));
  const convEvts = convEvents.map((e) => ({
    type: 'conversationEvent' as const,
    event: e,
  }));
  const aeEvts = aeEvents
    .map((e) => ({
      type: 'event' as const,
      event: e,
    }))
    .filter((e) => mayShowAEEvent(e.event.name, showRestrictedEvents));
  return [...msgs, ...convEvts, ...aeEvts].sort(
    (a, b) => getTime(a) - getTime(b)
  );
}

export function mayShowAEEvent(
  el: ActionExecutionEventType,
  showRestrictedEvents: boolean
): boolean {
  if (!isRestrictedEvent(el)) return true;
  return showRestrictedEvents;
}

function isRestrictedEvent(el: ActionExecutionEventType): boolean {
  return [
    ActionExecutionEventType.SUPERVISOR_TOOL_USED,
    ActionExecutionEventType.SUPERVISOR_REASONING,
    ActionExecutionEventType.REMINDER,
    ActionExecutionEventType.RAW_AGENT_MESSAGE,
    ActionExecutionEventType.RAW_SUPERVISOR_MESSAGE,
  ].includes(el);
}

function getTime(event: TranscriptItem): number {
  switch (event.type) {
    case 'message':
      return event.message.createdAt.getTime();
    case 'conversationEvent':
      return event.event.createdAt.getTime();
    case 'event':
      return event.event.createdAt.getTime();
    default:
      return assertUnreachable(event);
  }
}

type ConversationMessageEventComponentProps = {
  event: ConversationMessageEvent;
  conversationStartTime?: Date;
  moreMenuOptions?: OptionProps[];
  aiSide?: 'left' | 'right';
  suppressSystemMessages?: boolean;
  onSeekToSeconds?: (seconds: number) => void;
};
export function ConversationMessageEventComponent(
  p: ConversationMessageEventComponentProps
) {
  switch (p.event.type) {
    case ConversationMessageEventType.VOICE_CONTRIBUTION:
      return (
        <VoiceContributionBubble
          contribution={p.event as VoiceContribution}
          conversationStartTime={p.conversationStartTime}
          moreMenuOptions={p.moreMenuOptions}
          onSeekToSeconds={p.onSeekToSeconds}
        />
      );
    case ConversationMessageEventType.EMAIL_CONTRIBUTION:
      return (
        <EmailContributionBubble contribution={p.event as EmailContribution} />
      );
    case ConversationMessageEventType.CHAT_MESSAGE:
      return (
        <ChatContribution
          suppressSystemMessages={p.suppressSystemMessages}
          contribution={p.event as ChatMessageEvent}
          aiSide={p.aiSide}
        />
      );
    case ConversationMessageEventType.SYSTEM_MESSAGE:
      return (
        <SystemMessageBubble
          message={p.event as SystemMessageEvent}
          suppressSystemMessages={p.suppressSystemMessages}
          aiSide={p.aiSide}
        />
      );
    case ConversationMessageEventType.UNKNOWN:
      return <UnknownEventBubble event={p.event as UnknownEvent} />;
    default:
      return null;
  }
}

export function roleToClassName(role: ConversationContributionRole) {
  switch (role) {
    case ConversationContributionRole.USER:
      return 'USER';
    case ConversationContributionRole.ASSISTANT:
      return 'ASSISTANT';
    case ConversationContributionRole.SYSTEM:
      return 'SYSTEM';
    default:
      return '';
  }
}

type VoiceContributionBubbleProps = {
  contribution: VoiceContribution;
  conversationStartTime?: Date;
  moreMenuOptions?: OptionProps[];
  onSeekToSeconds?: (seconds: number) => void;
};
const VoiceContributionBubble = ({
  contribution,
  conversationStartTime,
  moreMenuOptions,
  onSeekToSeconds,
}: VoiceContributionBubbleProps) => {
  const isSeekable =
    !!onSeekToSeconds &&
    !!conversationStartTime &&
    contribution.role !== ConversationContributionRole.SYSTEM;
  const seekToContribution = () => {
    if (!conversationStartTime || window.getSelection()?.toString()) return;
    onSeekToSeconds?.(
      secondsBetween(conversationStartTime, contribution.createdAt)
    );
  };
  const roleClassName = roleToClassName(contribution.role);
  const className = clsx(el`voice-contribution`, {
    [roleClassName]: true,
    'is-seekable': isSeekable,
  });
  const wrapperClassName = clsx(el`voice-contribution-wrapper`, {
    [roleClassName]: true,
  });

  const { hoverProps, isHovered } = useHover({});
  const showMoreMenu = isHovered && (moreMenuOptions?.length || 0) > 0;

  const showOnLeft = contribution.role !== ConversationContributionRole.USER;

  return (
    <div className={wrapperClassName} {...hoverProps}>
      {showMoreMenu && showOnLeft && (
        <MoreMenu
          moreIcon={<LuEllipsisVertical size={12} />}
          options={moreMenuOptions}
        />
      )}
      <div
        className={className}
        onClick={isSeekable ? seekToContribution : undefined}
      >
        <VoiceContributionText contribution={contribution} />

        <Text type="body-xs" className="text-end">
          {conversationStartTime
            ? formatElapsedTime(conversationStartTime, contribution.createdAt)
            : formatAbsoluteTime(contribution.createdAt)}
        </Text>
      </div>
      {showMoreMenu && !showOnLeft && (
        <MoreMenu
          moreIcon={<LuEllipsisVertical size={12} />}
          options={moreMenuOptions}
        />
      )}
    </div>
  );
};

type VoiceContributionTextProps = {
  contribution: VoiceContribution;
};
const VoiceContributionText = ({
  contribution,
}: VoiceContributionTextProps) => {
  switch (contribution.role) {
    case ConversationContributionRole.SYSTEM:
      return <SystemMessageTranscript message={contribution.transcript} />;
    case ConversationContributionRole.ASSISTANT:
      return <Text type="body-xs">{contribution.transcript}</Text>;
    case ConversationContributionRole.USER:
      return <VoiceContributionTranscript contribution={contribution} />;
    default:
      throw new Error(`Unknown contribution role: ${contribution.role}`);
  }
};

type SystemMessageTranscriptProps = {
  message: string;
};
const SystemMessageTranscript = ({ message }: SystemMessageTranscriptProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleExpanded = () => setIsExpanded(!isExpanded);

  const textClassName = clsx('flex-1 w-full', {
    'line-clamp-1': !isExpanded,
  });
  return (
    // TODO(mike): This is a very quick pass at cleaning up system messages.
    // Leaves a lot to be desired (clearer statement of tool name, etc).
    <div className="relative">
      <span className="flex max-w-full flex-row break-words">
        <Text type="body-xs" className={textClassName}>
          {message}
        </Text>
      </span>
      <div className="absolute right-0 top-0">
        <Button
          size="xs"
          isGhost
          onPress={toggleExpanded}
          icon={
            isExpanded ? (
              <ChevronUp className="text-indigo-400" />
            ) : (
              <ChevronDown className="text-indigo-400" />
            )
          }
        />
      </div>
    </div>
  );
};

type EmailContributionBubbleProps = {
  contribution: EmailContribution;
};
const EmailContributionBubble = ({
  contribution,
}: EmailContributionBubbleProps) => {
  return (
    <div className={el`email-contribution`}>
      <Card key={`email-conv-${contribution.id}`} className="p-2 text-xs">
        <div className="flex flex-row gap-2 text-gray-500">
          <div className="flex w-full flex-col text-gray-500">
            <div className="flex w-full justify-between">
              <div className="line-clamp-1">From: {contribution.sender}</div>{' '}
              <div className="line-clamp-1">
                {formatAbsoluteTime(contribution.createdAt)}
              </div>
            </div>
            <div className="line-clamp-1">
              To: {contribution.toRecipients.join('-')}
            </div>
            <div className="line-clamp-1">
              Subject: {contribution.subject ?? '<no subject>'}
            </div>
          </div>
        </div>
        <div
          className="mt-2 h-[200px] overflow-y-auto"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(contribution.sanitizedHtmlBody),
          }}
        ></div>
      </Card>
    </div>
  );
};

type UnknownEventBubbleProps = {
  event: UnknownEvent;
};
const UnknownEventBubble = ({ event }: UnknownEventBubbleProps) => {
  return (
    <div className={el`unknown-event`}>
      <Text key={event.id} type="body-xs">
        Unknown or unsupported event
      </Text>
      <Text type="body-xs" className="text-end">
        {formatAbsoluteTime(event.createdAt)}
      </Text>
    </div>
  );
};

type SystemMessageBubbleProps = {
  message: SystemMessageEvent;
  suppressSystemMessages?: boolean;
  aiSide?: 'left' | 'right';
};
export const SystemMessageBubble = ({
  message,
  suppressSystemMessages,
  aiSide,
}: SystemMessageBubbleProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleExpanded = () => setIsExpanded(!isExpanded);
  const textClassName = clsx('flex-1 w-full', {
    'line-clamp-1': !isExpanded,
  });
  const assistantClassName =
    aiSide === 'right' ? 'ASSISTANT-RIGHT' : 'ASSISTANT-LEFT';

  const wrapperClassName = clsx(el`system-message`, assistantClassName);

  const strippedMessage = message.message
    .replace(/<SYSTEM MESSAGE>/g, '')
    .trim();

  const preview = strippedMessage.split('\n')[0];

  const messageText = isExpanded ? strippedMessage : preview;

  if (suppressSystemMessages) return null;

  return (
    <div className={wrapperClassName} onClick={toggleExpanded}>
      <Text key={message.id} type="body-xs" className={textClassName}>
        {messageText}
      </Text>

      <Text type="body-xs" className="text-end">
        {formatAbsoluteTime(message.createdAt)}
      </Text>
    </div>
  );
};

function messageProbablyBelongsToThisExecution(
  startDate?: Date,
  endDate?: Date | null
): (cEvent: ConversationMessageEvent) => boolean {
  return function (cEvent: ConversationMessageEvent) {
    if (!startDate) {
      return true;
    }
    const messageReceivedTime = cEvent.createdAt;

    if (!messageReceivedTime) {
      return false;
    }

    if (!endDate) {
      return messageReceivedTime.getTime() > startDate.getTime();
    }
    return (
      messageReceivedTime.getTime() < endDate.getTime() &&
      messageReceivedTime.getTime() > startDate.getTime()
    );
  };
}

function conversationEventProbablyBelongsToThisExecution(
  startDate?: Date,
  endDate?: Date | null
): (cEvent: ConversationEvent) => boolean {
  return function (cEvent: ConversationEvent) {
    if (!startDate) return true;
    const eventCreatedTime = cEvent.createdAt;
    if (!eventCreatedTime) return false;
    if (!endDate) return eventCreatedTime.getTime() > startDate.getTime();
    return (
      eventCreatedTime.getTime() < endDate.getTime() &&
      eventCreatedTime.getTime() > startDate.getTime()
    );
  };
}
